import {
  invalidateRecordCaches,
  invalidateRecordCachesExceptSubject
} from '@Data/hooks/recordCacheInvalidation';
import {
  EMPTY_FILES,
  type FileMetadataSnapshot,
  type FileMetadataStore
} from '@Pages/pageRuntime/fileMetadataStore';
import type { QueryClient } from '@tanstack/react-query';

import { CalendarLinkType } from '@/types/enums';

import { canPerformResourceOperation } from './authorization';
import {
  disabledAnalyticsMethods,
  disabledEndpointMessage,
  disabledResourceMethods
} from './endpointGating';
import { decorateResourceFields } from './relationFields';
import { normalizeSort } from './sortNormalization';
import type {
  AnalyticsDistributionParams,
  AnalyticsMetric,
  AnalyticsKpiParams,
  AnalyticsKpiResult,
  AnalyticsLeaderboardParams,
  AnalyticsRow,
  AnalyticsTimeSeriesParams,
  CalendarLinks,
  PageResourceData,
  ResourceCollectionResult,
  ResourceBulkUpdateRecord,
  ResourceCountOptions,
  ResourceListParams,
  ResourceRecordResult,
  ResourceRecordsResult,
  ResourceSearchBody,
  ResourceUrls,
  SdkApi,
  SdkResource,
  SdkResourceCapabilityFieldMappings,
  SdkResourceField,
  SdkResourceWorkflowInputs
} from './types';

const MAX_SEARCH_ALL_RECORDS = 5000;

/** Mirrors the server-side `per_page` ceiling (`DocumentPagination::MAX_PER_PAGE`). */
export const MAX_PER_PAGE = 500;

function normalizeSearchBody(input: ResourceSearchBody): ResourceSearchBody {
  const { perPage, filter, sort, scope, collapse, ...normalized } = input;

  if (perPage !== undefined && normalized.per_page === undefined) {
    normalized.per_page = perPage;
  }

  if (filter !== undefined && normalized.$filter === undefined) {
    normalized.$filter = filter;
  }

  if (sort !== undefined && normalized.$sort === undefined) {
    normalized.$sort = normalizeSort(sort);
  }
  if (scope !== undefined && normalized.$scope === undefined) {
    normalized.$scope = scope;
  }
  if (collapse !== undefined && normalized.$collapse === undefined) {
    normalized.$collapse = collapse;
  }

  return normalized;
}

/**
 * Filter and sort keys the list endpoint silently ignores: `IndexRecords` only reads
 * `page`, `per_page`, `relations`, and `$scope`. Accepting them would hand back an
 * unfiltered page that looks like a filtered one, so `list` rejects them outright.
 */
const LIST_UNSUPPORTED_KEYS = ['filter', '$filter', 'sort', '$sort'] as const;

function assertListParamsSupported(params: ResourceListParams | undefined): void {
  if (params === undefined) {
    return;
  }

  const unsupported = LIST_UNSUPPORTED_KEYS.filter((key) => params[key] !== undefined);

  if (unsupported.length === 0) {
    return;
  }

  throw new Error(
    `resource.list() does not support ${unsupported.join(', ')} — the list endpoint ignores filtering and sorting. Use resource.search(...) or Sdk.hooks.useRecords(resource, ...) instead.`
  );
}

function normalizeListParams(input?: ResourceListParams): ResourceListParams | undefined {
  if (input === undefined) {
    return undefined;
  }

  const { perPage, ...normalized } = input;

  if (perPage !== undefined && normalized.per_page === undefined) {
    normalized.per_page = perPage;
  }

  return normalized;
}

function normalizeCountParams(input?: ResourceCountOptions): Record<string, unknown> | undefined {
  if (input === undefined) {
    return undefined;
  }

  const { filter, scope, ...normalized } = input;

  if (filter !== undefined && normalized.$filter === undefined) {
    normalized.$filter = filter;
  }
  if (scope !== undefined && normalized.$scope === undefined) {
    normalized.$scope = scope;
  }

  return normalized;
}

function serializeCountParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }

    searchParams.set(key, key === '$filter' ? JSON.stringify(value) : String(value));
  }

  return searchParams.toString();
}

function assertSearchAllCap(maxRecords: number): void {
  if (!Number.isInteger(maxRecords) || maxRecords < 1 || maxRecords > MAX_SEARCH_ALL_RECORDS) {
    throw new Error(
      `resource.searchAll() maxRecords must be an integer between 1 and ${MAX_SEARCH_ALL_RECORDS}.`
    );
  }
}

/**
 * The wire format sent by PHP: capability mappings reference fields by their
 * `key`, not resolved field objects. This type is intentionally local to the
 * resolution step so the rest of the codebase only deals with the resolved form.
 */
type RawCapabilityFieldMappings = Record<string, string>;

/**
 * The PHP catalog stores field keys for capability mappings. This function
 * resolves those keys to the actual field objects from the resource's keyed
 * fields map for every capability present in the catalog. References whose key
 * does not match any visible catalog field are silently dropped.
 */
function resolveCapabilityMappings(
  rawCapabilities: Record<string, RawCapabilityFieldMappings> | undefined | null,
  fields: Record<string, SdkResourceField>
): Record<string, SdkResourceCapabilityFieldMappings> | undefined {
  if (!rawCapabilities) {
    return undefined;
  }

  const resolved: Record<string, SdkResourceCapabilityFieldMappings> = {};

  for (const [capKey, rawMappings] of Object.entries(rawCapabilities)) {
    const resolvedMappings: SdkResourceCapabilityFieldMappings = {};

    for (const [fieldReference, fieldKey] of Object.entries(rawMappings)) {
      const field = fields[fieldKey];
      if (field) {
        resolvedMappings[fieldReference] = field;
      }
    }

    if (Object.keys(resolvedMappings).length > 0) {
      resolved[capKey] = resolvedMappings;
    }
  }

  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

export function buildResourceHandle(
  metadata: PageResourceData,
  api: SdkApi,
  catalog: Record<string, PageResourceData> = {},
  queryClient?: QueryClient,
  fileStore?: FileMetadataStore
): SdkResource {
  const base = `/api/data/resources/${encodeURIComponent(metadata.id)}`;
  const recordUrl = (recordId: string) => `${base}/records/${encodeURIComponent(recordId)}`;
  const countUrl = `${base}/records/count`;
  const analyticsUrl = (endpoint: string) => `${base}/analytics/${endpoint}`;

  // Single source of truth for the record API endpoints: the handle methods below call
  // through these, and they are also exposed on the handle so authors can hit endpoints
  // the bundled methods don't cover.
  const urls: ResourceUrls = {
    index: `${base}/records`,
    search: `${base}/record/search`,
    count: countUrl,
    store: `${base}/records`,
    bulk: `${base}/records/bulk`,
    show: recordUrl,
    update: recordUrl,
    delete: recordUrl,
    restore: (recordId: string) => `${recordUrl(recordId)}/restore`,
    field: (fieldId: string) => `${base}/fields/${encodeURIComponent(fieldId)}`
  };

  // Relation fields arrive as bare `settings.resource_id` pointers; decorate them so
  // `resource.fields.family.fields.name` resolves against the flat catalog on demand.
  const fields = decorateResourceFields(metadata.fields, catalog);

  // Destructure raw capabilities and fields out so the spread below never leaks the
  // PHP field-key mappings or the undecorated field map.
  const { capabilities: rawCapabilities, fields: _rawFields, ...restMetadata } = metadata;
  const capabilities = resolveCapabilityMappings(rawCapabilities, fields);

  // PHP serializes an empty workflow-input map as a JSON array; normalize to the
  // object shape the SDK types promise.
  const rawWorkflowInputs = (metadata as { workflow_inputs?: unknown }).workflow_inputs;
  const workflowInputs = (
    rawWorkflowInputs && typeof rawWorkflowInputs === 'object' && !Array.isArray(rawWorkflowInputs)
      ? rawWorkflowInputs
      : {}
  ) as SdkResourceWorkflowInputs;

  const registerFiles = (key: string, result: ResourceCollectionResult) =>
    fileStore?.register(`${metadata.id}:${key}`, result) ?? result;

  const handle: SdkResource = {
    ...restMetadata,
    fields,
    workflow_inputs: workflowInputs,
    capabilities,
    urls,
    can: (operation) => canPerformResourceOperation(metadata, operation),
    list: (params?: ResourceListParams) => {
      assertListParamsSupported(params);

      return api
        .get<ResourceCollectionResult>(urls.index, {
          params: normalizeListParams(params)
        })
        .then((result) =>
          registerFiles(`list:${JSON.stringify(normalizeListParams(params))}`, result)
        );
    },
    search: (body: ResourceSearchBody) =>
      api
        .post<ResourceCollectionResult>(urls.search, normalizeSearchBody(body))
        .then((result) =>
          registerFiles(`search:${JSON.stringify(normalizeSearchBody(body))}`, result)
        ),
    searchAll: async ({ maxRecords, ...body }) => {
      assertSearchAllCap(maxRecords);

      const normalizedBody = normalizeSearchBody(body);
      const perPage = Math.min(normalizedBody.per_page ?? MAX_PER_PAGE, MAX_PER_PAGE, maxRecords);
      const records = [];
      const fileSnapshots: FileMetadataSnapshot[] = [];
      const fileOwner = `${metadata.id}:searchAll:${JSON.stringify({ ...normalizedBody, maxRecords })}`;
      let page = 1;
      let didWarn = false;

      while (records.length < maxRecords) {
        const result = await api.post<ResourceCollectionResult>(urls.search, {
          ...normalizedBody,
          page,
          per_page: perPage
        });

        if (fileStore) {
          const received = fileStore.receive(result);
          fileSnapshots.push({
            files: result.files ?? EMPTY_FILES,
            revision: received.fileMetadataRevision ?? 0
          });
          fileStore.replace(fileOwner, [...fileSnapshots]);
        }
        records.push(...result.data.slice(0, maxRecords - records.length));

        if (!didWarn && result.pagination.total > maxRecords) {
          // oxlint-disable-next-line no-console -- Truncated aggregate data must be visible to page authors.
          console.warn(
            `resource.searchAll() returned the first ${maxRecords} of ${result.pagination.total} matching records. Narrow the filter or raise maxRecords (maximum ${MAX_SEARCH_ALL_RECORDS}).`
          );
          didWarn = true;
        }

        if (
          result.data.length === 0 ||
          result.pagination.current_page >= result.pagination.last_page
        ) {
          break;
        }

        page += 1;
      }

      return records;
    },
    count: (options?: ResourceCountOptions) =>
      api.get<number>(urls.count, {
        params: normalizeCountParams(options),
        paramsSerializer: { serialize: serializeCountParams }
      }),
    get: (recordId: string) => api.get<ResourceRecordResult>(urls.show(recordId)),
    // Mutations invalidate cached record and analytics data across resources on
    // success because relation-backed queries can depend on the changed record.
    // Delete and restore skip the mutated record's own per-record queries — refetching
    // them under the flipped soft-delete scope would just 404.
    create: (payload: Record<string, unknown>) =>
      api.post<ResourceRecordResult>(urls.store, payload).then((response) => {
        if (queryClient) {
          void invalidateRecordCaches(queryClient);
        }
        return response;
      }),
    update: (recordId: string, payload: Record<string, unknown>) =>
      api.put<ResourceRecordResult>(urls.update(recordId), payload).then((response) => {
        if (queryClient) {
          void invalidateRecordCaches(queryClient);
        }
        return response;
      }),
    updateMany: (records: readonly ResourceBulkUpdateRecord[]) => {
      if (records.length === 0) {
        return Promise.resolve({ data: [] });
      }

      return api.put<ResourceRecordsResult>(urls.bulk, { records }).then((response) => {
        if (queryClient) {
          void invalidateRecordCaches(queryClient);
        }
        return response;
      });
    },
    delete: (recordId: string, payload) => {
      const request =
        payload === undefined
          ? api.delete<ResourceRecordResult>(urls.delete(recordId))
          : api.delete<ResourceRecordResult>(urls.delete(recordId), { data: payload });

      return request.then((response) => {
        if (queryClient) {
          void invalidateRecordCachesExceptSubject(queryClient, metadata.id, recordId);
        }
        return response;
      });
    },
    restore: (recordId: string) =>
      api.post<ResourceRecordResult>(urls.restore(recordId)).then((response) => {
        if (queryClient) {
          void invalidateRecordCachesExceptSubject(queryClient, metadata.id, recordId, {
            subjectRecord: response?.data ?? null
          });
        }
        return response;
      }),
    updateField: (field, payload) => {
      const fieldHandle = typeof field === 'string' ? fields[field] : field;

      if (!fieldHandle) {
        throw new Error(
          `resource.updateField() does not know the field "${String(field)}" on ${metadata.name} — pass a key from resource.fields or the field handle itself.`
        );
      }

      if (!fieldHandle.id) {
        throw new Error(
          `resource.updateField() cannot update "${fieldHandle.key}" — it has no field id, so it is not a stored workspace field.`
        );
      }

      const body = payload.type === undefined ? { ...payload, type: fieldHandle.type } : payload;

      return api.put<SdkResourceField>(urls.field(fieldHandle.id), body).then((updated) => {
        const current = fields[fieldHandle.key];
        if (current && updated && typeof updated === 'object') {
          // Refresh in place rather than replacing the object: relation decoration
          // installed lazy getters on it, and page code may hold references.
          const { type, label, description, guard, settings } = updated;
          Object.assign(current, { type, label, description, guard, settings });
        }
        return updated;
      });
    },
    invalidate: () => (queryClient ? invalidateRecordCaches(queryClient) : Promise.resolve()),

    // Analytics bodies use the endpoints' own keys (`date_field`, `$filter`, …) and are
    // sent unchanged — the server rejects unknown keys with a 422.
    analytics: {
      kpi: <const TMetrics extends readonly AnalyticsMetric[]>(
        params: AnalyticsKpiParams<TMetrics>
      ) => api.post<AnalyticsKpiResult<TMetrics>>(analyticsUrl('kpi'), params),
      timeSeries: (params: AnalyticsTimeSeriesParams) =>
        api.post<AnalyticsRow[]>(analyticsUrl('time-series'), params),
      leaderboard: (params: AnalyticsLeaderboardParams) =>
        api.post<AnalyticsRow[]>(analyticsUrl('leaderboard'), params),
      distribution: (params: AnalyticsDistributionParams) =>
        api.post<AnalyticsRow[]>(analyticsUrl('distribution'), params)
    }
  };

  if (capabilities?.eventable) {
    // Mirrors the `workspace.calendar` route (`calendar/{type}/{resource}/{record}`);
    // the type segment is constrained server-side to CalendarLinkType values.
    const calendarLink = (type: CalendarLinkType, recordId: string) =>
      `/calendar/${type}/${encodeURIComponent(metadata.id)}/${encodeURIComponent(recordId)}`;

    handle.calendarLinks = (recordId: string): CalendarLinks => ({
      google: calendarLink(CalendarLinkType.Google, recordId),
      apple: calendarLink(CalendarLinkType.Apple, recordId),
      outlook: calendarLink(CalendarLinkType.Outlook, recordId)
    });
  }

  applyDisabledEndpoints(handle, metadata);

  return handle;
}

/**
 * Replace the methods behind disabled endpoints with ones that explain why.
 *
 * The generated editor types omit these methods, so a typed page never reaches
 * here; this is for pages written before the endpoint was turned off, where the
 * alternative is an unexplained 404 from deep inside the SDK.
 */
function applyDisabledEndpoints(
  handle: { analytics: object },
  metadata: Pick<PageResourceData, 'name' | 'disabled_data_api_endpoints'>
): void {
  const disabledEndpoints = metadata.disabled_data_api_endpoints ?? [];

  if (disabledEndpoints.length === 0) {
    return;
  }

  const throwFor = (name: string, endpoint: string) => () => {
    throw new Error(disabledEndpointMessage(metadata.name, name, endpoint));
  };

  for (const { method, endpoint } of disabledResourceMethods(disabledEndpoints)) {
    Object.assign(handle, { [method]: throwFor(method, endpoint) });
  }

  for (const { method, endpoint } of disabledAnalyticsMethods(disabledEndpoints)) {
    Object.assign(handle.analytics, { [method]: throwFor(`analytics.${method}`, endpoint) });
  }
}

/**
 * Build the keyed `Sdk.resource(...)` handle map from a resource catalog. Each
 * handle is decorated against the full catalog so relation fields resolve. Shared
 * so consumers outside the page renderer can build handles from the same
 * `ResourceSdk::catalog()` payload without duplicating the loop.
 */
export function buildResourceHandles(
  resources: Record<string, PageResourceData>,
  api: SdkApi,
  queryClient: QueryClient,
  fileStore?: FileMetadataStore
): Record<string, SdkResource> {
  const resourceHandles: Record<string, SdkResource> = {};

  for (const [id, metadata] of Object.entries(resources)) {
    resourceHandles[id] = buildResourceHandle(metadata, api, resources, queryClient, fileStore);
  }

  return resourceHandles;
}
